import {Component, OnInit, Input, Inject, ElementRef, PLATFORM_ID, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {NgOptimizedImage, NgForOf, isPlatformBrowser} from '@angular/common';
import { JsonLdService } from '../../services/json-ld.service';
import {WordpressService} from "../../services/wordpress.service";
import {first} from "rxjs";
import {BlogList} from "../../models/blogList.model";
import {RouterLink} from "@angular/router";

@Component({
  selector: 'app-event-slider',
  templateUrl: './event-slider.component.html',
  styleUrls: ['./event-slider.component.less'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    RouterLink,
    NgForOf,
    NgOptimizedImage
  ]
})
export class EventSliderComponent implements OnInit {
  @Input() currentArticle: number | undefined;
  images: {link: string, id: number}[] = [];
  posts: BlogList[] = [];

  constructor(
    private el: ElementRef,
    private WordPressService: WordpressService,
    private jsonLdService: JsonLdService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
  }

  ngOnInit(): void {
    this.WordPressService.getPosts(5).pipe(first()).subscribe(posts => {
      this.posts = posts;
      this.getEventListJson(this.posts);
      setTimeout(() => this.initSwiper(), 0);
    })
  }

  private initSwiper(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const swiperEl = this.el.nativeElement.querySelector('swiper-container');
    if (!swiperEl) return;
    Object.assign(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 20,
      autoplay: { delay: 3000, disableOnInteraction: false },
      pagination: { clickable: true },
      autoHeight: true,
      breakpoints: { 1100: { slidesPerView: 2, spaceBetween: 20 } },
    });
    swiperEl.initialize();
  }

  getImageById(id: number) {
    return this.images.find((image) => image.id === id)?.link;
  }

  formatDateToISOForGermany(date: Date): string {
    // Berechne den UTC-Offset für Deutschland
    const offsetMinutes = -date.getTimezoneOffset(); // Automatisch berechnet (inkl. Sommer/Winterzeit)
    const offsetHours = Math.floor(offsetMinutes / 60);
    const offsetMins = Math.abs(offsetMinutes % 60);
    const offsetSign = offsetHours >= 0 ? "+" : "-";

    // Hole die Datumsteile
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Monate sind 0-basiert
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // Baue den Offset-String
    const offsetString = `${offsetSign}${String(Math.abs(offsetHours)).padStart(2, "0")}:${String(offsetMins).padStart(2, "0")}`;

    // Kombiniere alles
    return `${year}-${month}-${day}T${hours}:${minutes}${offsetString}`;
  }

  getEventListJson(posts: BlogList[]) {
    const events = posts.map((post, index) => {
      const originalDateString = post.acf?.date;
      const originalDate = new Date(originalDateString);

      const modifiedDate = new Date(originalDate);
      modifiedDate.setHours(23, 59, 0, 0);

      const image = post.yoast_head_json?.og_image?.[0]?.url || '';

      return {
        "@type": "Event",
        "@id": `https://dart-rock-cafe.de/magazin/${post.slug}`,
        "url": `https://dart-rock-cafe.de/magazin/${post.slug}`,
        "name": post.title.rendered,
        "description": post.content.rendered.replace(/<[^>]+>/g, ''), // HTML entfernen
        "startDate": this.formatDateToISOForGermany(originalDate),
        "endDate": this.formatDateToISOForGermany(modifiedDate),
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "eventStatus": "https://schema.org/EventScheduled",
        "image": [image],
        "location": {
          "@type": "Place",
          "name": "Dart-Rock-Café",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Hauptstraße 46",
            "addressLocality": "Lohfelden",
            "postalCode": "34253",
            "addressRegion": "Kassel",
            "addressCountry": "DE"
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": "Dart-Rock-Café",
          "url": "https://dart-rock-cafe.de"
        },
        "offers": {
          "@type": "Offer",
          "url": `https://dart-rock-cafe.de/magazin/${post.slug}`,
          "price": post.acf?.ticket_price || "10",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "validFrom": new Date().toISOString()
        },
        "position": index + 1
      };
    });

    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EventSeries",
          "@id": "https://dart-rock-cafe.de/#eventlist",
          "name": "Kommende Events im Dart Rock Café",
          "event": events.map(event => ({
            "@type": "Event",
            "@id": event["@id"],
            "name": event.name,
            "url": event.url,
            "description": event.description,
            "startDate": event.startDate,
            "endDate": event.endDate,
            "eventAttendanceMode": event.eventAttendanceMode,
            "eventStatus": event.eventStatus,
            "image": event.image,
            "location": event.location,
            "organizer": event.organizer,
            "offers": event.offers
          }))
        },
        ...events
      ]
    };

    this.jsonLdService.addData(jsonLd);
  }


}
